import '../scss/particle-background.scss'

interface Particle {
    x: number
    y: number
    vx: number
    vy: number
    opacity: number
    baseOpacity: number
    size: number
    depth: number
    gold: boolean
    phase: number
    textMask: number
    renderedX: number
    renderedY: number
}

interface TextRect {
    left: number
    top: number
    right: number
    bottom: number
}

const element = document.getElementById('avianese-particle-background')
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
const finePointer = window.matchMedia('(pointer: fine)')
const blueParticle = '198,212,236'
const goldParticle = '212,178,122'
const blueLink = '150,175,220'
const cursorLink = '190,205,235'
let destroyField: (() => void) | undefined

function createField(): () => void {
    if (!element) return () => undefined

    const mobile = window.matchMedia('(max-width: 720px)').matches
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    if (!context) return () => undefined

    const drawingContext: CanvasRenderingContext2D = context

    canvas.className = 'avianese-dot-field'
    canvas.setAttribute('aria-hidden', 'true')
    element.appendChild(canvas)

    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2)
    const particleCount = mobile ? 110 : 300
    const linkDistance = mobile ? 88 : 118
    const brightness = 1.08
    const frameInterval = 1000 / 30
    let width = 0
    let height = 0
    let mouseCenterX = 0
    let mouseCenterY = 0
    let textRects: TextRect[] = []
    let textRectFrame = 0
    let animationFrame = 0
    let lastDrawTime = 0
    let running = !document.hidden
    const mouse = { x: 0, y: 0, active: false }
    const particles: Particle[] = Array.from({ length: particleCount }, () => ({
        x: 0,
        y: 0,
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2,
        opacity: 0,
        baseOpacity: 0.2 + Math.random() * 0.3,
        size: (mobile ? 1.4 : 1.8) + Math.random() * 1.6,
        depth: 0.4 + Math.random(),
        gold: Math.random() < 0.12,
        phase: Math.random() * Math.PI * 2,
        textMask: 1,
        renderedX: 0,
        renderedY: 0,
    }))

    function resize(): void {
        width = window.innerWidth
        height = window.innerHeight
        canvas.width = Math.max(1, width * pixelRatio)
        canvas.height = Math.max(1, height * pixelRatio)
        drawingContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    }

    function refreshTextRects(): void {
        textRects = Array.from(document.querySelectorAll<HTMLElement>(
            'h1, h2, h3, p, li, .eyebrow, .sub, .body-large, .btn, .stat, .trust-row, .pronounce',
        )).flatMap(target => {
            const rect = target.getBoundingClientRect()

            if (
                rect.bottom < 0
                || rect.top > height
                || rect.right < 0
                || rect.left > width
                || rect.width < 2
                || rect.height < 2
            ) {
                return []
            }

            return [{
                left: rect.left - 16,
                top: rect.top - 10,
                right: rect.right + 16,
                bottom: rect.bottom + 10,
            }]
        })
    }

    function queueTextRects(): void {
        if (textRectFrame) return

        textRectFrame = requestAnimationFrame(() => {
            textRectFrame = 0
            refreshTextRects()
        })
    }

    function handleMouseMove(event: MouseEvent): void {
        mouse.x = event.clientX
        mouse.y = event.clientY
        mouse.active = true
    }

    function handleMouseOut(): void {
        mouse.active = false
    }

    function handleVisibilityChange(): void {
        running = !document.hidden

        if (running && !animationFrame) {
            animationFrame = requestAnimationFrame(draw)
        }
    }

    function draw(now: number): void {
        animationFrame = 0
        if (!running) return

        if (lastDrawTime && now - lastDrawTime + 0.5 < frameInterval) {
            animationFrame = requestAnimationFrame(draw)
            return
        }

        lastDrawTime = now

        drawingContext.clearRect(0, 0, width, height)
        mouseCenterX += ((mouse.active ? mouse.x / width - 0.5 : 0) - mouseCenterX) * 0.08
        mouseCenterY += ((mouse.active ? mouse.y / height - 0.5 : 0) - mouseCenterY) * 0.08

        const time = now * 0.001

        for (const particle of particles) {
            if (mouse.active) {
                const deltaX = particle.x - mouse.x
                const deltaY = particle.y - mouse.y
                const distanceSquared = deltaX * deltaX + deltaY * deltaY

                if (distanceSquared < 21000 && distanceSquared > 1) {
                    const force = 24 / distanceSquared
                    particle.vx += deltaX * force
                    particle.vy += deltaY * force
                }
            }

            particle.vx += (Math.random() - 0.5) * 0.02
            particle.vy += (Math.random() - 0.5) * 0.02

            const speed = Math.hypot(particle.vx, particle.vy) || 0.001
            const baseSpeed = mobile ? 0.16 : 0.22
            particle.vx = particle.vx / speed * baseSpeed
            particle.vy = particle.vy / speed * baseSpeed
            particle.x += particle.vx + Math.cos(time * 0.12 + particle.phase) * 0.16
            particle.y += particle.vy + Math.sin(time * 0.1 + particle.phase * 1.3) * 0.13

            if (particle.x < -16) particle.x = width + 16
            if (particle.x > width + 16) particle.x = -16
            if (particle.y < -16) particle.y = height + 16
            if (particle.y > height + 16) particle.y = -16

            particle.opacity += (particle.baseOpacity - particle.opacity) * 0.025
            particle.renderedX = particle.x + mouseCenterX * 40 * particle.depth
            particle.renderedY = particle.y + mouseCenterY * 40 * particle.depth
        }

        for (let firstIndex = 0; firstIndex < particles.length; firstIndex += 1) {
            const first = particles[firstIndex]
            if (first.textMask < 0.2) continue

            for (let secondIndex = firstIndex + 1; secondIndex < particles.length; secondIndex += 1) {
                const second = particles[secondIndex]
                if (second.textMask < 0.2) continue

                const deltaX = first.renderedX - second.renderedX
                const deltaY = first.renderedY - second.renderedY
                const distanceSquared = deltaX * deltaX + deltaY * deltaY

                if (distanceSquared >= linkDistance * linkDistance) continue

                const opacity = (1 - Math.sqrt(distanceSquared) / linkDistance) * 0.18 * brightness
                if (opacity < 0.01) continue

                drawingContext.strokeStyle = `rgba(${blueLink},${opacity})`
                drawingContext.lineWidth = 0.6
                drawingContext.beginPath()
                drawingContext.moveTo(first.renderedX, first.renderedY)
                drawingContext.lineTo(second.renderedX, second.renderedY)
                drawingContext.stroke()
            }
        }

        if (mouse.active) {
            for (const particle of particles) {
                const deltaX = particle.renderedX - mouse.x
                const deltaY = particle.renderedY - mouse.y
                const distance = Math.hypot(deltaX, deltaY)

                if (distance >= 150) continue

                drawingContext.strokeStyle = `rgba(${cursorLink},${(1 - distance / 150) * 0.26 * brightness})`
                drawingContext.lineWidth = 0.6
                drawingContext.beginPath()
                drawingContext.moveTo(particle.renderedX, particle.renderedY)
                drawingContext.lineTo(mouse.x, mouse.y)
                drawingContext.stroke()
            }
        }

        for (const particle of particles) {
            const obscuredByText = textRects.some(rect => (
                particle.renderedX > rect.left
                && particle.renderedX < rect.right
                && particle.renderedY > rect.top
                && particle.renderedY < rect.bottom
            ))

            particle.textMask += ((obscuredByText ? 0 : 1) - particle.textMask) * 0.2

            const opacity = particle.opacity * particle.textMask
            if (opacity < 0.012) continue

            if (particle.gold) {
                const speed = Math.hypot(particle.vx, particle.vy) || 0.001
                drawingContext.strokeStyle = `rgba(${goldParticle},${opacity * brightness * 0.45})`
                drawingContext.lineWidth = 0.8
                drawingContext.beginPath()
                drawingContext.moveTo(particle.renderedX, particle.renderedY)
                drawingContext.lineTo(
                    particle.renderedX - particle.vx / speed * 9,
                    particle.renderedY - particle.vy / speed * 9,
                )
                drawingContext.stroke()
            }

            drawingContext.beginPath()
            drawingContext.arc(particle.renderedX, particle.renderedY, particle.size, 0, Math.PI * 2)
            drawingContext.fillStyle = `rgba(${particle.gold ? goldParticle : blueParticle},${Math.min(1, opacity * brightness)})`
            drawingContext.fill()
        }

        animationFrame = requestAnimationFrame(draw)
    }

    resize()
    particles.forEach(particle => {
        particle.x = Math.random() * width
        particle.y = Math.random() * height
    })
    window.addEventListener('resize', resize, { passive: true })
    window.addEventListener('resize', queueTextRects, { passive: true })
    window.addEventListener('scroll', queueTextRects, { passive: true })
    document.addEventListener('visibilitychange', handleVisibilityChange)

    if (finePointer.matches) {
        window.addEventListener('mousemove', handleMouseMove, { passive: true })
        window.addEventListener('mouseout', handleMouseOut, { passive: true })
    }

    window.setTimeout(refreshTextRects, 60)
    animationFrame = requestAnimationFrame(draw)

    return () => {
        running = false
        cancelAnimationFrame(animationFrame)
        cancelAnimationFrame(textRectFrame)
        window.removeEventListener('resize', resize)
        window.removeEventListener('resize', queueTextRects)
        window.removeEventListener('scroll', queueTextRects)
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseout', handleMouseOut)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        canvas.remove()
    }
}

function start(): void {
    if (!element || reducedMotion.matches || destroyField) return
    destroyField = createField()
}

function stop(): void {
    destroyField?.()
    destroyField = undefined
}

function handleMotionPreference(): void {
    if (reducedMotion.matches) {
        stop()
        return
    }

    start()
}

function teardown(): void {
    reducedMotion.removeEventListener('change', handleMotionPreference)
    window.removeEventListener('pagehide', stop)
    window.removeEventListener('pageshow', start)
    stop()
}

if (element) {
    reducedMotion.addEventListener('change', handleMotionPreference)
    window.addEventListener('pagehide', stop)
    window.addEventListener('pageshow', start)
    start()
}

if (import.meta.hot) {
    import.meta.hot.dispose(teardown)
}
